import { ref, Ref, onMounted } from 'vue'

export interface Language {
  code: string
  name: string
  flag: string
}

export interface LanguageSwitcherState {
  currentLanguage: Ref<string>
  isOpen: Ref<boolean>
  availableLanguages: Language[]
  toggleMenu: () => void
  switchLanguage: (langCode: string) => void
  closeMenu: () => void
}

const SUPPORTED_LANGUAGES = ['en', 'nl'] as const
type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number]

const COOKIE_NAME = 'preferredLanguage'
const STORAGE_KEY = 'preferredLanguage'
const COOKIE_MAX_AGE = 31536000 // 1 year in seconds

/**
 * Get cookie value by name
 */
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? decodeURIComponent(match[2]) : null
}

/**
 * Check if a language code is supported
 */
function isSupportedLanguage(lang: string): lang is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage)
}

export default function useLanguageSwitcher(): LanguageSwitcherState {
  const currentLanguage = ref<string>('en')
  const isOpen = ref<boolean>(false)

  const availableLanguages: Language[] = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱' }
  ]

  /**
   * Initialize current language - priority: cookie > HTML lang > localStorage > default
   */
  onMounted(() => {
    // Priority 1: Check cookie (this is what the server uses)
    const cookieLang = getCookie(COOKIE_NAME)
    if (cookieLang && isSupportedLanguage(cookieLang)) {
      currentLanguage.value = cookieLang
      return
    }

    // Priority 2: Check HTML lang attribute (set by server based on detection)
    const htmlLang = document.documentElement.lang
    if (htmlLang) {
      const lang = htmlLang.split('-')[0].toLowerCase()
      if (isSupportedLanguage(lang)) {
        currentLanguage.value = lang
        return
      }
    }

    // Priority 3: Check localStorage
    const storedLang = localStorage.getItem(STORAGE_KEY)
    if (storedLang && isSupportedLanguage(storedLang)) {
      currentLanguage.value = storedLang
      return
    }

    // Default: English
    currentLanguage.value = 'en'
  })

  /**
   * Toggle the language menu dropdown
   */
  const toggleMenu = (): void => {
    isOpen.value = !isOpen.value
  }

  /**
   * Close the language menu dropdown
   */
  const closeMenu = (): void => {
    isOpen.value = false
  }

  /**
   * Switch to a different language
   */
  const switchLanguage = (langCode: string): void => {
    if (langCode === currentLanguage.value) {
      closeMenu()
      return
    }

    if (!isSupportedLanguage(langCode)) {
      console.warn(`[LanguageSwitcher] Unsupported language: ${langCode}`)
      return
    }

    // Store preference in cookie (server can read this)
    document.cookie = `${COOKIE_NAME}=${langCode}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`

    // Also store in localStorage as backup
    localStorage.setItem(STORAGE_KEY, langCode)

    // Reload the page to apply the new language
    window.location.reload()
  }

  return {
    currentLanguage,
    isOpen,
    availableLanguages,
    toggleMenu,
    switchLanguage,
    closeMenu
  }
}
